import { LscApiError } from './api';

export const LSC_COMMISSIONER_CREATION_ERROR_CODES = [
  'league_creation_requires_subscription',
  'free_member_create_league_blocked',
  'commissioner_capacity_exhausted',
  'commissioner_subscription_inactive',
] as const;

export type LscCommissionerCreationErrorCode =
  (typeof LSC_COMMISSIONER_CREATION_ERROR_CODES)[number];

export const LSC_ENTITLEMENT_ERROR_CODES = [
  'entitlement_missing',
  'entitlement_read_only',
  'entitlement_suspended',
  'entitlement_feature_disabled',
  'entitlement_inactive',
] as const;

export type LscEntitlementErrorCode = (typeof LSC_ENTITLEMENT_ERROR_CODES)[number];

export const LSC_ACTIVATION_PERMISSION_ERROR_CODES = [
  'billing_inactive',
  'forbidden',
  'authentication_required',
  'league_archived',
  'activation_blocked',
  'not_commissioner',
] as const;

export type LscActivationPermissionErrorCode =
  (typeof LSC_ACTIVATION_PERMISSION_ERROR_CODES)[number];

export interface ParsedLscMutationError {
  message: string;
  showBillingLink: boolean;
  showPricingLink: boolean;
}

const ENTITLEMENT_GUIDANCE: Record<
  LscEntitlementErrorCode,
  { message: string; nextStep: string; showBillingLink: boolean; showPricingLink: boolean }
> = {
  entitlement_missing: {
    message: 'This league does not have active League Stream Center access yet.',
    nextStep:
      'Subscribe or link your active subscription from League Settings to continue.',
    showBillingLink: true,
    showPricingLink: true,
  },
  entitlement_read_only: {
    message: 'This league is in read-only mode because billing needs attention.',
    nextStep: 'Update payment in Manage billing to continue making changes.',
    showBillingLink: true,
    showPricingLink: false,
  },
  entitlement_suspended: {
    message: 'League Stream Center access is suspended for this league.',
    nextStep: 'Manage billing or contact support before making changes.',
    showBillingLink: true,
    showPricingLink: false,
  },
  entitlement_feature_disabled: {
    message: 'This feature is not enabled for the current LSC plan.',
    nextStep: 'Contact support if this looks wrong.',
    showBillingLink: false,
    showPricingLink: false,
  },
  entitlement_inactive: {
    message: 'This league doesn’t have active LSC access.',
    nextStep: 'Finish billing or bind the subscription, then try again.',
    showBillingLink: true,
    showPricingLink: true,
  },
};

const ACTIVATION_PERMISSION_GUIDANCE: Record<
  LscActivationPermissionErrorCode,
  ParsedLscMutationError
> = {
  billing_inactive: {
    message:
      'An active subscription is required before activating automations. Finish billing or bind your league to continue.',
    showBillingLink: true,
    showPricingLink: true,
  },
  forbidden: {
    message:
      'Only league commissioners can perform this action. Ask a commissioner if you need help.',
    showBillingLink: false,
    showPricingLink: false,
  },
  authentication_required: {
    message: 'Sign in with Twitch to continue.',
    showBillingLink: false,
    showPricingLink: false,
  },
  league_archived: {
    message: 'This league is archived. Restore it before making setup or activation changes.',
    showBillingLink: false,
    showPricingLink: false,
  },
  activation_blocked: {
    message: 'Complete the setup steps listed above before activating automations.',
    showBillingLink: false,
    showPricingLink: false,
  },
  not_commissioner: {
    message: 'Only the league owner can link billing to this league.',
    showBillingLink: false,
    showPricingLink: false,
  },
};

const COMMISSIONER_CREATION_GUIDANCE: Record<
  LscCommissionerCreationErrorCode,
  ParsedLscMutationError
> = {
  league_creation_requires_subscription: {
    message:
      'Creating a league requires an active commissioner subscription. View commissioner plans to continue.',
    showBillingLink: false,
    showPricingLink: true,
  },
  free_member_create_league_blocked: {
    message:
      'Creating a league requires an active commissioner subscription. Join a league for free or subscribe to start one.',
    showBillingLink: false,
    showPricingLink: true,
  },
  commissioner_capacity_exhausted: {
    message:
      'Your subscription is already linked to the maximum number of leagues. Manage billing to review your plan.',
    showBillingLink: true,
    showPricingLink: false,
  },
  commissioner_subscription_inactive: {
    message:
      'An active commissioner subscription is required to create a league. Manage billing to restore access.',
    showBillingLink: true,
    showPricingLink: true,
  },
};

export function isLscCommissionerCreationErrorCode(
  code?: string
): code is LscCommissionerCreationErrorCode {
  return LSC_COMMISSIONER_CREATION_ERROR_CODES.includes(
    code as LscCommissionerCreationErrorCode
  );
}

export function getLscCommissionerCreationGuidance(
  error: LscApiError
): ParsedLscMutationError | null {
  if (!error.code || !isLscCommissionerCreationErrorCode(error.code)) {
    return null;
  }
  const guidance = COMMISSIONER_CREATION_GUIDANCE[error.code];
  return {
    ...guidance,
    message: error.message?.trim() || guidance.message,
  };
}

export function isLscEntitlementErrorCode(code?: string): code is LscEntitlementErrorCode {
  return LSC_ENTITLEMENT_ERROR_CODES.includes(code as LscEntitlementErrorCode);
}

export function isLscActivationPermissionErrorCode(
  code?: string
): code is LscActivationPermissionErrorCode {
  return LSC_ACTIVATION_PERMISSION_ERROR_CODES.includes(
    code as LscActivationPermissionErrorCode
  );
}

export function getLscEntitlementErrorGuidance(
  error: LscApiError
): ParsedLscMutationError | null {
  if (!error.code || !isLscEntitlementErrorCode(error.code)) {
    return null;
  }
  const guidance = ENTITLEMENT_GUIDANCE[error.code];
  const backendMessage = error.message?.trim();
  const message = backendMessage
    ? `${guidance.message} ${guidance.nextStep}`
    : `${guidance.message} ${guidance.nextStep}`;
  return {
    message,
    showBillingLink: guidance.showBillingLink,
    showPricingLink: guidance.showPricingLink,
  };
}

export function getLscActivationPermissionGuidance(
  error: LscApiError
): ParsedLscMutationError | null {
  if (!error.code || !isLscActivationPermissionErrorCode(error.code)) {
    return null;
  }
  const guidance = ACTIVATION_PERMISSION_GUIDANCE[error.code];
  const backendMessage = error.message?.trim();
  if (backendMessage && error.code === 'activation_blocked') {
    return {
      ...guidance,
      message: backendMessage,
    };
  }
  return guidance;
}

export function parseLscMutationError(err: unknown, fallback: string): ParsedLscMutationError {
  if (err instanceof LscApiError) {
    if (err.code === 'vod_sync_apply_not_in_canary' || err.code === 'vod_sync_apply_not_eligible') {
      return {
        message:
          err.message?.trim() ||
          "Saving to the review queue isn't included for this league. Preview still works. If this league should be able to save, contact support.",
        showBillingLink: false,
        showPricingLink: false,
      };
    }
    if (err.code === 'not_found') {
      return {
        message:
          err.message?.trim() ||
          "This VOD isn’t in the queue anymore. Go back to VOD Sync and run a new sync if you still need it.",
        showBillingLink: false,
        showPricingLink: false,
      };
    }
    const entitlement = getLscEntitlementErrorGuidance(err);
    if (entitlement) {
      return entitlement;
    }
    const commissionerCreation = getLscCommissionerCreationGuidance(err);
    if (commissionerCreation) {
      return commissionerCreation;
    }
    const permission = getLscActivationPermissionGuidance(err);
    if (permission) {
      return permission;
    }
    if (err.status === 401) {
      return ACTIVATION_PERMISSION_GUIDANCE.authentication_required;
    }
    if (err.status === 403 && (!err.code || err.code === 'forbidden')) {
      return ACTIVATION_PERMISSION_GUIDANCE.forbidden;
    }
    return {
      message: err.message || fallback,
      showBillingLink: false,
      showPricingLink: false,
    };
  }
  return {
    message: fallback,
    showBillingLink: false,
    showPricingLink: false,
  };
}

export function formatBindLeagueError(err: unknown): string {
  if (!(err instanceof LscApiError)) {
    return 'Could not bind subscription to this league. Please try again or contact support.';
  }
  switch (err.code) {
    case 'ambiguous_multiple_leagues':
      return 'Your subscription could apply to multiple leagues. Open League Settings for this league and link it explicitly.';
    case 'already_bound_other_league':
      return 'This subscription is already linked to another league. One subscription covers one league.';
    case 'inactive_subscription':
      return 'An active subscription is required before linking a league.';
    case 'not_commissioner':
      return 'Only the league commissioner can link a subscription to this league.';
    case 'comped_entitlement_preserved':
      return 'This league has comped access and cannot be linked to a paid subscription.';
    case 'billing_customer_missing':
      return 'No billing account was found for your account. Subscribe first from pricing or League Settings.';
    default:
      return err.message || 'Could not bind subscription to this league. Please try again or contact support.';
  }
}

export function isActivePaidAccountStatus(
  status?: { subscription_status?: string | null; entitlement?: { subscription_status?: string } | null } | null
): boolean {
  if (!status) return false;
  const subStatus = (
    status.entitlement?.subscription_status ||
    status.subscription_status ||
    ''
  )
    .toLowerCase()
    .trim();
  return subStatus === 'active' || subStatus === 'trialing';
}
